// WebMCP (Web Model Context Protocol) types.
//
// WebMCP lets an in-browser AI agent — a browser-native assistant, an
// extension, or an MCP client bridged into the tab — call tools that this page
// registers on `document.modelContext`. The API is a W3C draft
// (https://webmachinelearning.github.io/webmcp/), so we keep our own minimal
// structural types instead of depending on shipped DOM lib types.

/** JSON Schema subset we use for tool inputs. */
export type JsonSchema = {
  type: "object";
  properties?: Record<string, unknown>;
  required?: string[];
  additionalProperties?: boolean;
};

export type WebMcpContent =
  | { type: "text"; text: string }
  | { type: "image"; data: string; mimeType: string };

export type WebMcpResult = {
  content: WebMcpContent[];
  structuredContent?: unknown;
  isError?: boolean;
};

export type WebMcpAnnotations = {
  /** Tool only reads page/site state — safe to call without confirmation. */
  readOnlyHint?: boolean;
  /** Tool mutates or sends something the user would want to confirm. */
  destructiveHint?: boolean;
  /** Same input always yields the same result. */
  idempotentHint?: boolean;
  /** Tool touches resources outside this origin. */
  openWorldHint?: boolean;
  /** Result contains third-party text the agent must not treat as instructions. */
  untrustedContentHint?: boolean;
};

export type WebMcpTool = {
  name: string;
  title?: string;
  description: string;
  inputSchema?: JsonSchema;
  annotations?: WebMcpAnnotations;
  execute: (
    input: Record<string, unknown>,
    options?: { signal?: AbortSignal },
  ) => Promise<WebMcpResult> | WebMcpResult;
};

/** Where a tool came from, used by the on-page inspector. */
export type WebMcpScope =
  | "site"
  | "library"
  | "gem"
  | "submit"
  | "contact"
  | "demo";

export type RegisteredTool = {
  tool: WebMcpTool;
  scope: WebMcpScope;
};

export type ModelContextLike = {
  registerTool: (
    tool: WebMcpTool,
    options?: { signal?: AbortSignal },
  ) => unknown;
  /** Declarative registration added to the draft in 2026: replaces the set. */
  provideContext?: (context: { tools: WebMcpTool[] }) => unknown;
  /** Explicit teardown counterpart to registerTool. */
  unregisterTool?: (name: string) => unknown;
  getTools?: () => unknown;
  executeTool?: (name: string, input: unknown, options?: unknown) => unknown;
  addEventListener?: (type: string, cb: () => void) => void;
  removeEventListener?: (type: string, cb: () => void) => void;
};

export type WebMcpAvailability = "unknown" | "native" | "polyfill" | "unavailable";

/** Convenience helpers for building results. */
export function textResult(text: string, structured?: unknown): WebMcpResult {
  return structured === undefined
    ? { content: [{ type: "text", text }] }
    : { content: [{ type: "text", text }], structuredContent: structured };
}

export function jsonResult(value: unknown, summary?: string): WebMcpResult {
  return {
    content: [
      { type: "text", text: summary ?? JSON.stringify(value, null, 2) },
    ],
    structuredContent: value,
  };
}

export function errorResult(message: string): WebMcpResult {
  return { content: [{ type: "text", text: message }], isError: true };
}
